import * as React from 'react'

import { Button, Text } from '@factorialco/deprecated-design-system'
import {
  DeleteShiftTemplateDocument,
  ShiftManagementTemplateFragment,
  ShiftManagementTemplatesDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { trackDeleteTemplateShiftManagementSubmit } from 'modules/shift_management/lib/trackEvents'

type ModalProps = React.ComponentProps<typeof Modal>

interface Props {
  onCancel: ModalProps['onClose']
  shiftTemplate: ShiftManagementTemplateFragment
}

const DeleteShiftTemplate = ({ shiftTemplate, onCancel }: Props) => {
  const { id, name } = shiftTemplate

  const title = i18n.t('shift_management.rotating_shifts.delete_rotating_shift')
  const description = i18n.t('shift_management.rotating_shifts.delete_unused', { name })
  const description2 = i18n.t('shift_management.rotating_shifts.delete_used_2')

  const [deleteShiftTemplate, { loading }] = useMutation(DeleteShiftTemplateDocument, {
    extractorFn: (data) => data.shiftManagementMutations.deleteShiftmanagementTemplate,
    refetchQueries: [ShiftManagementTemplatesDocument],
  })

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const result = await deleteShiftTemplate({ variables: { id } })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      trackDeleteTemplateShiftManagementSubmit(shiftTemplate?.id)
      app.setFlash({ type: 'correct', message: i18n.t('common.removed_successfully') })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('common.removed_failed') })
    }

    closePortal()
  }

  return (
    <Modal
      illustration="delete-cross"
      title={title}
      description={description}
      onClose={onCancel}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onCancel}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" disabled={loading} submit>
            {i18n.t('common.delete')}
          </Button>
        </>
      }
    >
      <Text.Normal>{description2}</Text.Normal>
    </Modal>
  )
}

export default observer(DeleteShiftTemplate)
